"use client";

import { useCallback } from "react";
import { Box, Button, Icon, VStack } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import useSWR from "swr";
import { LuDownload } from "react-icons/lu";
import { atsResumeService } from "@/_services/resumeServices/ats-resume";
import { useGenerateATSResume } from "@/_components/ui/ResumeUI/_hooks/useGenerateATSResume";
import { GenerateATSResumeDialog } from "@/_components/ui/ResumeUI/DetailModal/GenerateATSResumeDialog";
import { ATSVersionContent } from "@/_components/ui/ResumeUI/DetailModal/ResumeInfoTabs/ATSVersionContent";
import { getATSApiKey } from "@/_components/ui/ResumeUI/DetailModal/ResumeInfoTabs/ResumeInfo";
import { downloadResumePdf } from "@/_components/ui/ResumeUI/_hooks/resumeDownload";

interface BenchmarkAuthenticatedATSPreviewProps {
  resumeId: string;
  historyId?: string;
  fileBasename: string;
  minH?: string;
  onDownload?: () => Promise<void>;
  isDownloading?: boolean;
  downloadLabel?: string;
}

export function BenchmarkAuthenticatedATSPreview({
  resumeId,
  historyId,
  fileBasename,
  minH = "500px",
  onDownload,
  isDownloading = false,
  downloadLabel,
}: BenchmarkAuthenticatedATSPreviewProps) {
  const t = useTranslations("resume_benchmark");
  const resolvedDownloadLabel = downloadLabel ?? t("ats.downloadPdf");

  const {
    data: atsData,
    isLoading: isLoadingAts,
    mutate: refetchAts,
  } = useSWR(
    getATSApiKey(resumeId),
    () => atsResumeService.getATSByResumeId(resumeId),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const handleATSSuccess = useCallback(() => {
    void refetchAts();
  }, [refetchAts]);

  const {
    isDialogOpen,
    jobStatus,
    progress,
    activeStepIndex,
    isGenerating,
    startGenerate,
    closeDialog,
    retryGenerate,
  } = useGenerateATSResume({
    resumeId,
    historyId,
    fileBasename,
    onSuccess: handleATSSuccess,
  });

  const handleDefaultDownload = useCallback(async () => {
    if (!atsData?.file_url) return;
    await downloadResumePdf(atsData.file_url, `${fileBasename}-ats`);
  }, [atsData?.file_url, fileBasename]);

  const handleDownloadClick = onDownload ?? handleDefaultDownload;
  const canDownload = Boolean(atsData?.file_url) || Boolean(onDownload);

  return (
    <VStack align="stretch" gap={4}>
      <Box
        rounded="2xl"
        overflow="auto"
        bg="blackAlpha.400"
        minH={minH}
        maxH="calc(100vh - 300px)"
        position="relative"
      >
        <GenerateATSResumeDialog
          open={isDialogOpen}
          status={jobStatus}
          progress={progress}
          activeStepIndex={activeStepIndex}
          onCancel={closeDialog}
          onRetry={() => void retryGenerate()}
        />

        <ATSVersionContent
          atsFileUrl={atsData?.file_url}
          isLoading={isLoadingAts}
          onGenerate={() => void startGenerate()}
          isGenerating={isGenerating}
        />
      </Box>

      {canDownload && atsData?.file_url ? (
        <Button
          alignSelf="center"
          size="md"
          colorPalette="gray"
          visual="outline"
          color="white"
          fontWeight="semibold"
          px={8}
          gap={2}
          loading={isDownloading}
          _hover={{
            bg: "whiteAlpha.100",
            borderColor: "whiteAlpha.400",
            color: "white",
          }}
          onClick={() => void handleDownloadClick()}
        >
          <Icon as={LuDownload} boxSize={4} />
          {resolvedDownloadLabel}
        </Button>
      ) : null}
    </VStack>
  );
}
